import { X, AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export function PendingMarksModal({ isOpen, onClose, pendingSubjects, classId }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200">
      <div 
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl animate-in slide-in-from-bottom-4 duration-300"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-divider flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
              <AlertTriangle size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-ink">Incomplete Marks</h2>
              <p className="text-xs text-graphite font-secondary">
                Cannot generate report cards until all subject marks are entered.
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-paper">
          <div className="space-y-4">
            {pendingSubjects.map(subjectInfo => (
              <div key={subjectInfo.subject} className="bg-white border border-divider rounded-xl overflow-hidden shadow-sm">
                <div className="px-5 py-3 border-b border-divider flex items-center justify-between bg-gray-50/50">
                  <div className="flex flex-col">
                    <span className="font-bold text-ink">{subjectInfo.subject}</span>
                    <span className="text-xs text-graphite font-medium font-secondary">
                      {subjectInfo.progress}% Complete
                    </span>
                  </div>
                  
                  {/* Progress Bar Mini */}
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-500 rounded-full" 
                      style={{ width: `${subjectInfo.progress}%` }} 
                    />
                  </div>
                </div>
                
                <div className="p-4 space-y-3">
                  <p className="text-sm font-semibold text-graphite">Missing Entries:</p>
                  <ul className="space-y-2">
                    {subjectInfo.missingComponents.map(comp => (
                      <li key={comp.componentId} className="flex items-start gap-2 text-sm text-ink bg-red-50/50 p-2 rounded border border-red-100">
                        <span className="text-red-500 mt-0.5">•</span>
                        <div>
                          <span className="font-medium text-red-800">{comp.label}</span>
                          <p className="text-xs text-red-600 mt-1">
                            Missing for {comp.missingCount} student{comp.missingCount > 1 ? 's' : ''} 
                            <span className="opacity-75" title={comp.missingStudents.join(', ')}> (Hover to view names)</span>
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="pt-2">
                    {subjectInfo.subject !== 'Admin Result Status' ? (
                      <Link
                        to={`/teacher/register/${classId}/${subjectInfo.subject}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
                      >
                        Go to {subjectInfo.subject} Register
                        <ArrowRight size={14} />
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500">
                        Admin must assign status in Students panel
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-divider bg-white">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-sm font-semibold transition-colors float-right"
          >
            Close & Review Later
          </button>
        </div>
      </div>
    </div>
  );
}
